/**
 * This class handles all events on both mobile and PC
 * These user intents will trigger custom omniCanvas events
 * @event omniCanvas.mousedown mouse has been clicked over the omniCanvas elementFromPoint
 * @event omniCanvas._rotate user is rotating an object
 * @event omniCanvas._scale user is scaling an object
 * @event omniCanvas._move user is moving an object
 * @event omniCanvas.mouseup mouse is no longer pressed
 */
import {Vector} from '@teemill/common/classes';

/**
 * Creates an OmniCanvasEvent from an Event
 * OmniCanvasEvent is a custom event object that relates to a browser event
 *
 * @param {Event} e A browser event
 * @param {OmniCanvas} controller An instance of an OmniCanvas
 * @param {Boolean} relativeToScale If true, event will be scaled relative to
 * the canvas' scale. If null, it will be assumed to be true.
 *
 * @return {OmniCanvasEvent}
 */
function OmniCanvasEvent(e, controller, relativeToScale) {
  let vct;
  let touches = e.touches;

  if (e.constructor.name === 'Touch') {
    touches = [e];
  } else if (!e.touches || !e.touches.length) {
    touches = e.changedTouches;
  }

  const rect = controller.canvasElement.getBoundingClientRect();

  if (!e) {
    return null;
  }

  if (typeof e.e !== 'undefined') {
    return e;
  }

  // Normalize coordinates to viewport (client) space before converting to
  // element-local coordinates. Using pageX/pageY with getBoundingClientRect()
  // caused an offset equal to the page scroll because pageX is document-
  // (scroll) relative while getBoundingClientRect() is viewport relative.
  let clientX = 0;
  let clientY = 0;

  const scrollX = typeof window !== 'undefined' ? window.scrollX || window.pageXOffset || 0 : 0;
  const scrollY = typeof window !== 'undefined' ? window.scrollY || window.pageYOffset || 0 : 0;

  if (!touches || touches.length === 0) {
    if (typeof e.clientX === 'number' && typeof e.clientY === 'number') {
      clientX = e.clientX;
      clientY = e.clientY;
    } else if (typeof e.offsetX === 'number' && typeof e.offsetY === 'number') {
      // offsetX/Y are relative to the target element; convert to client coords
      clientX = e.offsetX + rect.left;
      clientY = e.offsetY + rect.top;
    } else if (typeof e.pageX === 'number' && typeof e.pageY === 'number') {
      // fallback: convert page (document) coords to client (viewport) coords
      clientX = e.pageX - scrollX;
      clientY = e.pageY - scrollY;
    }
  } else {
    const t = touches[0];
    if (typeof t.clientX === 'number' && typeof t.clientY === 'number') {
      clientX = t.clientX;
      clientY = t.clientY;
    } else if (typeof t.pageX === 'number' && typeof t.pageY === 'number') {
      clientX = t.pageX - scrollX;
      clientY = t.pageY - scrollY;
    }
  }

  vct = new Vector(clientX - rect.left, clientY - rect.top);

  vct = vct.scale(controller.getPixelScale());

  if (controller.boundingBox) {
    vct.x -= controller.boundingBox.position.x;
    vct.y -= controller.boundingBox.position.y;
  }

  vct = vct.translate(
    (controller.boundingBox.width / 2) * (controller.params.scale - 1),
    (controller.boundingBox.height / 2) * (controller.params.scale - 1)
  );

  if (relativeToScale !== false) {
    vct = vct.scale(1 / controller.params.scale);
  }

  // we have to translate the event position to adjust for the focal
  // point of the canvas
  const distanceToMidPoint = controller.renderer
    .getDeltaFromBoundsMidPointToCanvasMidPoint()
    .scale(controller.params.scale - 1);

  vct = vct.translate(distanceToMidPoint.x / 2, distanceToMidPoint.y / 2);

  vct.e = e;

  return vct;
}

const EventHandlers = {
  /**
   * Handles the mousedown event
   *
   * @param {Event} event The browser event object
   * @param {OmniCanvas} controller The OmniCanvas on which the event took place
   * @return {void}
   */
  mousedown(event, controller) {
    if (!controller.params.stage.evented) {
      return;
    }

    // Preventing default on mousedown can stop native focus updates in some
    // browsers (e.g. Firefox), so focus the canvas explicitly for keyboard
    // shortcuts.
    if (controller.canvasElement) {
      if (!controller.canvasElement.hasAttribute('tabindex')) {
        controller.canvasElement.setAttribute('tabindex', '0');
      }
      controller.canvasElement.focus?.({preventScroll: true});
    }

    event.preventDefault();
    event = new OmniCanvasEvent(event, controller);
    const previousElement = controller.activeEl;

    controller.activeEl = controller.findObjectAtPosition(event);

    if (controller.activeEl && controller.activeEl.class === 'objectControls') {
      const control = controller.activeEl;
      control.mouseDown(event);
      controller.activeControlObject = control;
      controller.activeEl = control.element;
    } else {
      controller.activeControlObject = null;
    }

    const activeElementHasChanged =
      previousElement?.id !== controller.activeEl?.id;

    if (activeElementHasChanged) {
      controller.activeEl?.select(event);
      previousElement?.deselect(event);
    }

    controller.eventMouseDown(event);
    controller.mouseState = 'down';

    if (event.e.target.closest('.omni-canvas')) {
      controller.prepareHistoryLog();
    }
  },

  /**
   * Handles the mousemove event
   *
   * @param {Event} e The browser event object
   * @param {OmniCanvas} controller The OmniCanvas on which the event took place
   * @return {void}
   */
  mousemove(e, controller) {
    if (!controller.params.stage.evented) {
      return;
    }

    if (e.touches) {
      switch (e.touches.length) {
        case 1:
          if (controller.mouseState === 'down') {
            if (controller.activeControlObject) {
              e.preventDefault();
              controller.eventObjectAction(e);
            } else if (
              controller.activeEl ||
              controller.params.stage.globalControlsEnabled
            ) {
              e.preventDefault();
              controller.eventMove(e);
            }
          }
          break;
        case 2:
          if (controller.mouseState === 'down') {
            if (
              controller.activeEl ||
              controller.params.stage.globalControlsEnabled
            ) {
              e.preventDefault();
              controller.eventPinch(e);
            }
          }
          break;
        default:
          break;
      }
    } else {
      e.preventDefault();
      if (controller.mouseState === 'down') {
        if (controller.activeControlObject) {
          controller.eventObjectAction(e);
        } else if (
          controller.activeEl ||
          controller.params.stage.globalControlsEnabled
        ) {
          controller.eventMove(e);
        }
      }

      e = new OmniCanvasEvent(e, controller);
      const el = controller.findObjectAtPosition(e);
      if (
        el &&
        (el.controllable || el.class === 'objectControls') &&
        el.customCursor
      ) {
        if (typeof el.customCursor === 'function') {
          document.body.style.cursor = el.customCursor(el);
        } else {
          document.body.style.cursor = el.customCursor;
        }
      } else {
        document.body.style.cursor = 'auto';
      }
    }
  },

  /**
   * Handles the mouseup event
   *
   * @param {Event} e The browser event object
   * @param {OmniCanvas} controller The OmniCanvas on which the event took place
   * @return {void}
   */
  mouseup(e, controller) {
    if (!controller.params.stage.evented) {
      return;
    }

    e = new OmniCanvasEvent(e, controller);
    controller.touchPoints = [];
    if (controller.mouseState === 'down') {
      controller.eventMouseUp(e);
    }

    if (controller.activeEl) {
      controller.activeEl.click(new OmniCanvasEvent(e, controller));
    }

    if (controller.preparedHistoryLog) {
      controller.commitHistoryLog();
    }

    if (controller.mouseState) {
      controller.render();
    }

    controller.mouseState = undefined;
  },
};

/**
 * Attach event listeners to an OmniCanvas
 *
 * @param {OmniCanvas} controller The OmniCanvas instance to attach event
 * listeners to
 */
class EventListeners {
  constructor(controller) {
    this.controller = controller;

    this.mousedown = this.mousedown.bind(this);
    this.mousemove = this.mousemove.bind(this);
    this.mouseout = this.mouseout.bind(this);
    this.mouseup = this.mouseup.bind(this);
    this.keydown = this.keydown.bind(this);
    this.keyup = this.keyup.bind(this);
    this.input = this.input.bind(this);
  }

  mousedown(e) {
    EventHandlers.mousedown(e, this.controller);
  }

  mousemove(e) {
    EventHandlers.mousemove(e, this.controller);
  }

  mouseout() {
    document.body.style.cursor = 'auto';
  }

  mouseup(e) {
    EventHandlers.mouseup(e, this.controller);
  }

  keydown(e) {
    if (this.controller.activeEl) {
      this.controller.eventKeyDown(e);
    }
  }

  async keyup(e) {
    await this.controller.eventKeyUp(e);
  }

  input(e) {
    if (this.controller.activeEl) {
      e.preventDefault();
      this.controller.eventKeyDown(e);
    }
  }

  attach() {
    const controller = this.controller;
    controller.canvasElement.addEventListener('mousedown', this.mousedown);
    controller.canvasElement.addEventListener('touchstart', this.mousedown);

    controller.canvasElement.addEventListener('mousemove', this.mousemove);
    controller.canvasElement.addEventListener('touchmove', this.mousemove);

    controller.canvasElement.addEventListener('mouseout', this.mouseout);

    document.addEventListener('mouseup', this.mouseup);
    document.addEventListener('touchend', this.mouseup);

    document.addEventListener('keydown', this.keydown);
    document.addEventListener('keyup', this.keyup);
    document.addEventListener('input', this.input);
  }

  detach() {
    const controller = this.controller;
    controller.canvasElement?.removeEventListener('mousedown', this.mousedown);
    controller.canvasElement?.removeEventListener('touchstart', this.mousedown);

    controller.canvasElement?.removeEventListener('mousemove', this.mousemove);
    controller.canvasElement?.removeEventListener('touchmove', this.mousemove);

    controller.canvasElement?.removeEventListener('mouseout', this.mouseout);

    document.removeEventListener('mouseup', this.mouseup);
    document.removeEventListener('touchend', this.mouseup);

    document.removeEventListener('keydown', this.keydown);
    document.removeEventListener('keyup', this.keyup);
    document.removeEventListener('input', this.input);
  }
}

export {OmniCanvasEvent, EventHandlers, EventListeners};
